import RouteError from '@/components/ui/route-error';
import { createFileRoute, useSearch } from '@tanstack/react-router';
import { z } from 'zod';
import { BILLING_TABS } from '@/types/route-tabs';
import { LuCreditCard, LuReceiptText } from 'react-icons/lu';
import { queryClient } from '@/lib/query-client';
import { useAppStore } from '@/stores/app-store';
import DashboardRouteLayout from '@/app/components/dashboard-route-layout';
import DashboardPageShell from '@/app/components/dashboard-page-shell';
import { Box } from '@/components/ui/lib/box';
import { Tabs } from '@/components/ui/lib/tabs';
import { useBoolean } from '@/components/ui/lib/use-boolean';
import { DICTIONARY } from '@/config/dictionary';
import { requireAuth } from '@/utils/require-auth-before-load';
import {
  useBalance,
  usePaymentMethods,
  useCreditThresholds,
  getPaymentMethodsQueryOptions,
} from '@/features/payments/api/billing-api';
import {
  useInvoiceRecords,
  useMonthlySpend,
  getInvoiceRecordsQueryOptions,
  getMonthlySpendQueryOptions,
} from '@/features/payments/api/invoice-records-api';
import BillingBalanceCard from '@/features/payments/components/billing-balance-card';
import PaymentMethodCard from '@/features/payments/components/payment-method-card';
import CreditThresholdsCard from '@/features/payments/components/credit-thresholds-card';
import TopUpCreditPanel from '@/features/payments/components/top-up-credit-panel';
import MonthlySpendChart from '@/features/payments/components/monthly-spend-chart';
import InvoiceRecordsTable from '@/features/payments/components/invoice-records-table';
import { useTeamMembers } from '@/features/team/api/team-api';

const DashboardBillingPage = () => {
  const d = DICTIONARY.dashboard.billing;
  const { tab } = useSearch({ from: '/billing' });
  const selectedWholesalerId = useAppStore((s) => s.selectedWholesalerId);

  const { data: balance, isLoading: balanceLoading } = useBalance();
  const { data: methodsData, isLoading: methodsLoading } = usePaymentMethods();
  const { data: thresholds, isLoading: thresholdsLoading } = useCreditThresholds();

  const { data: invoiceData, isLoading: invoicesLoading } = useInvoiceRecords(selectedWholesalerId);
  const { data: spendData, isLoading: spendLoading } = useMonthlySpend(selectedWholesalerId);
  const { data: teamData } = useTeamMembers(selectedWholesalerId);

  const memberMap = Object.fromEntries((teamData?.members ?? []).map((m) => [m.id, m.displayName]));
  const invoices = invoiceData?.invoices ?? [];
  const months = spendData?.months ?? [];
  const methods = methodsData?.methods ?? [];

  const [showTopUp, setShowTopUp] = useBoolean(false);

  const handleToggleTopUp = () => {
    setShowTopUp.toggle();
  };

  return (
    <DashboardRouteLayout>
      <DashboardPageShell title={d.pageTitle} description={d.pageDescription}>
        <Tabs.Root defaultValue={tab ?? BILLING_TABS[0]} variant="line" lazyMount unmountOnExit>
          <Tabs.List mb="6">
            <Tabs.Trigger value={BILLING_TABS[0]}>
              <LuCreditCard />
              {d.tabs.credit}
            </Tabs.Trigger>
            <Tabs.Trigger value={BILLING_TABS[1]}>
              <LuReceiptText />
              {d.tabs.receipts}
            </Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value={BILLING_TABS[0]}>
            <Box display="flex" flexDirection="column" gap="4">
              <Box
                display="grid"
                gridTemplateColumns={{ base: '1fr', md: '1fr 1fr' }}
                gap="4"
                alignItems="stretch"
              >
                <BillingBalanceCard
                  balance={balance}
                  isLoading={balanceLoading}
                  showTopUp={showTopUp}
                  onToggleTopUp={handleToggleTopUp}
                />
                <PaymentMethodCard methods={methods} isLoading={methodsLoading} />
              </Box>

              {showTopUp && <TopUpCreditPanel onSuccess={setShowTopUp.off} />}

              <CreditThresholdsCard thresholds={thresholds} isLoading={thresholdsLoading} />
            </Box>
          </Tabs.Content>

          <Tabs.Content value={BILLING_TABS[1]}>
            <Box display="flex" flexDirection="column" gap="4">
              <MonthlySpendChart data={months} isLoading={spendLoading} />
              <InvoiceRecordsTable
                invoices={invoices}
                isLoading={invoicesLoading}
                memberMap={memberMap}
              />
            </Box>
          </Tabs.Content>
        </Tabs.Root>
      </DashboardPageShell>
    </DashboardRouteLayout>
  );
};

const billingSearchSchema = z.object({
  tab: z.enum(BILLING_TABS).optional(),
});

export const Route = createFileRoute('/billing')({
  validateSearch: billingSearchSchema,
  beforeLoad: requireAuth,
  loader: async ({ location }) => {
    const { selectedWholesalerId, currentUser } = useAppStore.getState();
    const firstWholesalerId = currentUser?.wholesalers[0]?.id ?? null;
    const selectedOrFirstWholesalerId = selectedWholesalerId ?? firstWholesalerId;
    const params = new URLSearchParams(location.search);
    const activeTab = (params.get('tab') ?? BILLING_TABS[0]) as (typeof BILLING_TABS)[number];

    // Balance and credit-thresholds are intentionally NOT prefetched here — their query
    // options use staleTime: 0, so useBalance/useCreditThresholds always refetch on mount
    // regardless of a prefetch, making a loader prefetch pure duplicate traffic (and, for a
    // freshly-activated wholesaler whose billing record the backend provisions
    // asynchronously, a second chance to race that provisioning and 404).
    const creditPrefetches =
      selectedOrFirstWholesalerId != null
        ? [queryClient.prefetchQuery(getPaymentMethodsQueryOptions(selectedOrFirstWholesalerId))]
        : [];

    const receiptsPrefetches =
      selectedOrFirstWholesalerId != null
        ? [
            queryClient.prefetchQuery(getInvoiceRecordsQueryOptions(selectedOrFirstWholesalerId)),
            queryClient.prefetchQuery(getMonthlySpendQueryOptions(selectedOrFirstWholesalerId)),
          ]
        : [];

    await Promise.allSettled(activeTab === BILLING_TABS[1] ? receiptsPrefetches : creditPrefetches);
    return null;
  },
  component: DashboardBillingPage,
  errorComponent: RouteError,
});
